/**
 * PasswordInput — reusable password field with show/hide toggle.
 *
 * Why a component: Login, Register, and ResetPassword all need the same
 * eye-toggle UX; centralising it avoids three separate copies of the same
 * useState + onClick logic and keeps the styling consistent.
 */
import { forwardRef, useState } from "react";
import { Eye, EyeOff } from "lucide-react";
import { Input } from "./ui/input";
import { useI18n } from "../i18n";

const PasswordInput = forwardRef(function PasswordInput(
  { className = "", "data-testid": testId, ...rest },
  ref,
) {
  const { t } = useI18n();
  const [visible, setVisible] = useState(false);
  return (
    <div className="relative">
      <Input
        ref={ref}
        type={visible ? "text" : "password"}
        className={`pr-10 ${className}`}
        data-testid={testId}
        {...rest}
      />
      <button
        type="button"
        onClick={() => setVisible((v) => !v)}
        data-testid={testId ? `${testId}-toggle` : "password-visibility-toggle"}
        aria-label={visible ? t("common.hidePassword") : t("common.showPassword")}
        className="absolute inset-y-0 right-2 my-auto h-7 w-7 grid place-items-center rounded-md text-stone-400 hover:text-stone-700 hover:bg-stone-100 transition-colors"
        tabIndex={-1}
      >
        {visible ? <EyeOff size={15} /> : <Eye size={15} />}
      </button>
    </div>
  );
});

export default PasswordInput;
